import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';

const { FiShield, FiGlobe, FiLock, FiUsers, FiKey, FiServer, FiArrowRight } = FiIcons;

function App() {
  const cloudKeywords = [
    'ENCRYPT',
    'SECURE',
    'PRIVATE',
    'OPEN',
    'FREE',
    'YOURS',
    'SAFE',
    'DISTRIBUTED',
    'ZERO-K'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-600 via-purple-700 to-orange-600 text-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4">
        <div className="absolute inset-0 bg-black/20"></div>
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center z-10 max-w-4xl"
        >
          <motion.h1 
            className="text-6xl md:text-8xl font-black mb-6 bg-gradient-to-r from-yellow-300 to-red-300 bg-clip-text text-transparent"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            DISTRIHOSTS
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl mb-8 font-light"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            The first truly decentralized cloud. Your data. Your keys. Your revolution.
          </motion.p>
          <motion.button 
            className="bg-yellow-400 text-black px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-300 transition-all transform hover:scale-105 shadow-2xl"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            JOIN THE REVOLUTION
          </motion.button>
        </motion.div>
        
        {/* Floating Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-yellow-400 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [-20, 20, -20],
                opacity: [0.3, 1, 0.3],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-black/40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-4xl md:text-6xl font-black text-center mb-16 bg-gradient-to-r from-red-300 to-yellow-300 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            BREAK FREE
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FiLock,
                title: "YOUR KEYS ONLY",
                description: "Zero-knowledge encryption. We can't see your data even if we wanted to."
              },
              {
                icon: FiGlobe,
                title: "GLOBALLY DISTRIBUTED",
                description: "Your data lives everywhere and nowhere. Unstoppable redundancy."
              },
              {
                icon: FiUsers,
                title: "TRULY DECENTRALIZED",
                description: "No single point of failure. No corporate overlords. Pure freedom."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="bg-gradient-to-br from-red-500/20 to-purple-600/20 p-8 rounded-2xl backdrop-blur-sm border border-white/10 hover:border-yellow-400/50 transition-all"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="bg-yellow-400 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={feature.icon} className="text-2xl text-black" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-yellow-300">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Revolution Stats */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            className="text-4xl md:text-5xl font-black mb-12 text-yellow-300"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            THE NUMBERS DON'T LIE
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { number: "0%", label: "CORPORATE CONTROL" },
              { number: "100%", label: "USER OWNERSHIP" },
              { number: "∞", label: "SCALABILITY" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gradient-to-br from-yellow-400/20 to-red-500/20 p-8 rounded-2xl border border-yellow-400/30"
              >
                <div className="text-6xl font-black text-yellow-300 mb-4">{stat.number}</div>
                <div className="text-xl font-bold">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Revolution */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-900/50 to-red-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-4xl md:text-5xl font-black text-center mb-16 text-yellow-300"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            HOW WE REVOLT
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="space-y-8">
                {[
                  {
                    icon: FiKey,
                    title: "Client-Side Encryption",
                    description: "Your data is encrypted before it leaves your device. Always."
                  },
                  {
                    icon: FiServer,
                    title: "Distributed Network",
                    description: "Thousands of nodes worldwide. No single point of failure."
                  },
                  {
                    icon: FiShield,
                    title: "Zero Knowledge",
                    description: "We literally cannot access your data. Mathematical impossibility."
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="bg-yellow-400 p-3 rounded-full flex-shrink-0">
                      <SafeIcon icon={item.icon} className="text-xl text-black" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-yellow-300">{item.title}</h3>
                      <p className="text-gray-300">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gradient-to-br from-red-500/20 to-purple-600/20 p-8 rounded-2xl backdrop-blur-sm border border-white/10">
                <div className="grid grid-cols-3 gap-4">
                  {cloudKeywords.map((keyword, i) => (
                    <motion.div
                      key={i}
                      className="bg-yellow-400/20 p-4 rounded-lg border border-yellow-400/30 flex flex-col items-center justify-center"
                      animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.5, 1, 0.5],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 0.2,
                      }}
                    >
                      <div className="text-xs font-bold text-yellow-300 mb-1">{keyword}</div>
                      <div className="w-6 h-6 bg-yellow-400/30 rounded-full flex items-center justify-center">
                        <div className="w-3 h-3 bg-yellow-300 rounded-full"></div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-4 text-center">
        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl md:text-7xl font-black mb-8 bg-gradient-to-r from-yellow-300 to-red-300 bg-clip-text text-transparent">
            READY TO REVOLT?
          </h2>
          <p className="text-xl md:text-2xl mb-12 text-gray-300">
            Join the movement. Take back your data. Own your cloud.
          </p>
          
          <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
            <motion.button 
              className="bg-gradient-to-r from-yellow-400 to-red-400 text-black px-12 py-6 rounded-full font-bold text-xl hover:shadow-2xl transition-all transform hover:scale-105 flex items-center gap-3"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              START YOUR REVOLUTION
              <SafeIcon icon={FiArrowRight} className="text-xl" />
            </motion.button>
            
            <motion.button 
              className="border-2 border-yellow-400 text-yellow-400 px-12 py-6 rounded-full font-bold text-xl hover:bg-yellow-400 hover:text-black transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              LEARN MORE
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-black/50 backdrop-blur-sm border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <motion.div
              className="text-5xl md:text-6xl font-black mb-4 bg-gradient-to-r from-yellow-300 to-red-300 bg-clip-text text-transparent"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              DISTRIHOSTS
            </motion.div>
            <p className="text-gray-400 mb-6">The cloud revolution starts here.</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-gray-400 mb-8">
            <a href="#" className="hover:text-yellow-400 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-yellow-400 transition-colors">Cookie Policy</a>
            <a href="#" className="hover:text-yellow-400 transition-colors">Terms & Conditions</a>
            <a href="#" className="hover:text-yellow-400 transition-colors">Acceptable Use Policy</a>
            <a href="#" className="hover:text-yellow-400 transition-colors">Security</a>
            <a href="#" className="hover:text-yellow-400 transition-colors">Compliance</a>
            <a href="#" className="hover:text-yellow-400 transition-colors">Manifesto</a>
            <a href="#" className="hover:text-yellow-400 transition-colors">Community</a>
            <a href="#" className="hover:text-yellow-400 transition-colors">Documentation</a>
            <a href="#" className="hover:text-yellow-400 transition-colors">Contact</a>
          </div>
          
          <div className="text-center text-gray-500 text-sm border-t border-white/10 pt-6">
            <p>&copy; 2024-2025 Distrihosts. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;